import { Grid, Stack } from '@mui/material';
import { LabelledText } from 'components/LabelledText/LabelledText';
import { MoreDropdown, DropdownOption } from 'components/Menu/MoreDropdown';
import { Panel } from 'components/Panel/Panel';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { BaseAudit, TemperatureDetails } from 'types/domainTypes';
import { ReactElement } from 'react';

function GetDisplayTemperature(value: number | undefined): string {
  if (value === undefined) return '';

  return `${value.toLocaleString(i18n.language)} °C`;
}

function isValidNumber(value: number | undefined): boolean {
  if (value === undefined) return false;

  return !isNaN(value);
}

function GetSummerExternal(temperature: TemperatureDetails): string {
  if (temperature.summerExternalColor) {
    return temperature.summerExternalColor.label;
  } else if (isValidNumber(temperature?.summerExternalCustom)) {
    return GetDisplayTemperature(temperature?.summerExternalCustom);
  } else {
    return '';
  }
}

interface TemperatureViewProps {
  temperature?: TemperatureDetails & BaseAudit;
  onEditClick: () => void;
  onDeleteClick: () => void;
}

export function TemperatureView({ temperature, onEditClick, onDeleteClick }: TemperatureViewProps): ReactElement | null {
  const { t } = useTranslation();

  if (!temperature) {
    return null;
  }

  const moreButton = (
    <MoreDropdown data-testid="temperature-details-dropdown">
      <DropdownOption callback={onEditClick} label={t('calculation-page.label-edit-temperature')} />
      <DropdownOption callback={onDeleteClick} label={t('calculation-page.label-remove-temperature')} />
    </MoreDropdown>
  );
  return (
    <Panel
      data-testid="temperature-panel"
      panelTitle={t('calculation-page.label-calculation-temperature')}
      panelSummary={t('common.summary-format', {
        createdBy: temperature.createdByName ? temperature.createdByName : temperature.createdBy,
        modifiedOn: temperature.modifiedOn,
        modifiedBy: temperature.modifiedByName ? temperature.modifiedByName : temperature.modifiedBy,
      })}
      panelButton={moreButton}
    >
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText label="calculation-page.label-summer-external" value={GetSummerExternal(temperature)} />
          <LabelledText label="calculation-page.label-summer-internal" value={GetDisplayTemperature(temperature.summerInternal)} />
        </Stack>
      </Grid>
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText label="calculation-page.label-winter-external" value={GetDisplayTemperature(temperature.winterExternal)} />
          <LabelledText label="calculation-page.label-winter-internal" value={GetDisplayTemperature(temperature.winterInternal)} />
        </Stack>
      </Grid>
    </Panel>
  );
}
